/**
 * Where a burst of twin replies is, counted from the queue the server is
 * actually working through.
 *
 * The client holds messages the server has not accepted yet. The server
 * answers one accepted message at a time and releases the next only when
 * that turn finishes. The position below is that fact: replies the server
 * has already finished in this burst, plus the one it is answering now,
 * plus the messages still waiting. It does not move because time passed.
 */

export interface QueueReplyProgress {
  /** 1-based index of the message the twin is replying to. */
  current: number
  /** Finished replies in this burst, plus the one in progress, plus those still queued. */
  total: number
}

export interface QueueReplyProgressInput {
  queueLength: number
  /**
   * The head of the queue is the send dispatched to the server and not yet
   * removed. It is not also "waiting".
   */
  headInFlight: boolean
  /**
   * Messages this client already handed the server whose turns have not
   * finished. Distinct from `headInFlight`: that one has not been accepted
   * yet. A later send can be in flight while an earlier accepted reply is
   * still open, and both stay in the total. The index stays on the oldest
   * unfinished reply until the server finishes it.
   */
  acceptedUnfinished: number
  /** Turns the server has finished since this burst opened. */
  completedInBurst: number
}

/**
 * One reply that is still the current queue position after this long gets an
 * inline notice. The position moves only when the server finishes a turn, so
 * the notice is not a timer standing in for progress.
 *
 * A minute is long enough that an ordinary reply has finished, and it is
 * earlier than the server turn timeout (120s), so the notice shows while
 * that reply is still being produced.
 */
export const SLOW_REPLY_NOTICE_MS = 60_000

export function describeQueueReplyProgress(
  input: QueueReplyProgressInput,
): QueueReplyProgress | null {
  const { queueLength, headInFlight, acceptedUnfinished, completedInBurst } = input
  if (queueLength < 0 || completedInBurst < 0 || acceptedUnfinished < 0) return null
  if (headInFlight && queueLength === 0) return null

  const inFlight = headInFlight ? 1 : 0
  const waiting = queueLength - inFlight
  if (waiting < 0) return null

  const open = acceptedUnfinished + inFlight
  if (open === 0) return null

  const total = completedInBurst + open + waiting
  // The oldest unfinished reply. A newer send does not move this; only a
  // server completion does, by increasing `completedInBurst`.
  const current = completedInBurst + 1
  if (total < 2 || current < 1 || current > total) return null
  return { current, total }
}
